import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiMessageCircle, FiShare2, FiBookmark, FiPlay, FiPause, FiVolume2, FiVolumeX } from 'react-icons/fi';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { MdVerified } from 'react-icons/md';
import { Link, useNavigate } from 'react-router-dom';
import { CATEGORIES, REACTIONS } from '../../utils/constants';
import { commentsAPI, reactionsAPI, favoritesAPI, postsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CommentSection from '../CommentSection/CommentSection';
import toast from 'react-hot-toast';
import { resolveMediaUrl } from '../../utils/media';
import './VideoModal.css';

const VideoModal = ({ video, onClose }) => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [comments, setComments] = useState([]);
  const [reactionCounts, setReactionCounts] = useState({});
  const [userReaction, setUserReaction] = useState(null);
  const videoRef = useRef(null);
  const posterUrl = video?.image ? resolveMediaUrl(video.image) : undefined;

  const category = CATEGORIES.find(cat => cat.id === video.category);

  useEffect(() => {
    loadData();
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video.id]);

  const loadData = async () => {
    try {
      const [commentsRes, reactionsRes, favRes, userReactionRes] = await Promise.all([
        commentsAPI.getByPostId(video.id),
        reactionsAPI.getByPostId(video.id).catch(() => ({ data: {} })),
        isAuthenticated ? favoritesAPI.check(video.id).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        isAuthenticated ? reactionsAPI.getUserReaction(video.id).catch(() => ({ data: {} })) : Promise.resolve({ data: {} })
      ]);
      setComments(commentsRes.data || []);
      setReactionCounts(reactionsRes.data?.reactions || reactionsRes.data?.counts || {});
      setUserReaction(userReactionRes?.data?.userReaction || null);
      setIsSaved(Boolean(favRes?.data?.isFavorite));
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleShare = () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para compartir');
      return;
    }
    const slugify = (text) => {
      if (!text) return '';
      return text
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    };

    const fallbackSlug = video?.id ? `${slugify(video?.title) || 'video'}-${video.id}` : (slugify(video?.title) || 'video');
    const url = `${window.location.origin}/video/${video.slug || fallbackSlug}`;
    if (navigator.share) {
      navigator.share({
        title: video.title,
        text: video.description,
        url
      });
    } else {
      navigator.clipboard.writeText(url);
      toast.success('¡Enlace copiado!');
    }
  };

  const handleReaction = async (reactionType) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para reaccionar');
      return;
    }
    try {
      const res = await reactionsAPI.toggle(video.id, reactionType);
      setUserReaction(res.data?.userReaction || null);
      setReactionCounts(res.data?.reactions || {});
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  const handleToggleSave = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar');
      return;
    }
    try {
      if (isSaved) {
        await favoritesAPI.remove(video.id);
        setIsSaved(false);
        toast.success('Eliminado de guardados');
      } else {
        await favoritesAPI.add(video.id);
        setIsSaved(true);
        toast.success('¡Guardado!');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('No se pudo actualizar guardados');
    }
  };

  const isOwner = Boolean(user?.id && video?.author?.id && user.id === video.author.id);
  const authorUsername = video?.author?.username || video?.author?.id || video?.author_id;
  const authorIsVerified = Boolean(
    video?.author?.verification_level && video.author.verification_level !== 'none'
  );

  const handleEdit = () => {
    navigate(`/posts/edit/${video.id}`, { replace: true, state: {} });
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar esta publicación? Esta acción no se puede deshacer.')) return;
    try {
      await postsAPI.deletePost(video.id);
      toast.success('Publicación eliminada');
      navigate('/');
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('No se pudo eliminar');
    }
  };

  const handleAddComment = async (commentData) => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para comentar');
      return;
    }
    try {
      await commentsAPI.create({
        postId: video.id,
        content: commentData.text,
        parentId: commentData.parentId
      });
      loadData();
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error('Error al agregar comentario');
    }
  };

  const handleEditComment = async (commentId, data) => {
    try {
      await commentsAPI.update(commentId, data);
      loadData();
    } catch (error) {
      toast.error('Error al editar');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await commentsAPI.delete(commentId);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="video-modal-overlay" onClick={onClose}>
      <div className="video-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <FiX />
        </button>

        <div className="video-modal-content">
          {/* Video Player */}
          <div className="video-modal-player">
            <video
              ref={videoRef}
              src={resolveMediaUrl(video.video_url)}
              poster={posterUrl}
              className="modal-video"
              loop
              playsInline
            />
            
            <div className="video-modal-controls">
              <button className="control-btn" onClick={togglePlay}>
                {isPlaying ? <FiPause /> : <FiPlay />}
              </button>
              <button className="control-btn" onClick={toggleMute}>
                {isMuted ? <FiVolumeX /> : <FiVolume2 />}
              </button>
              <span className="video-time">{video.duration}</span>
            </div>

            {category && (
              <div className="video-modal-category" style={{ background: category.color }}>
                {category.icon} {category.name}
              </div>
            )}
          </div>

          {/* Info & Comments */}
          <div className="video-modal-sidebar">
            <div className="video-modal-info">
              <h2>{video.title}</h2>
              <p>{video.description || video.excerpt}</p>
              
              <div className="video-modal-author">
                <Link to={authorUsername ? `/${authorUsername}` : '/'}>
                  <img src={resolveMediaUrl(video.author?.avatar)} alt={video.author?.name} />
                </Link>
                <div>
                  <Link to={authorUsername ? `/${authorUsername}` : '/'}>
                    <strong>
                      {video.author?.name}
                      {authorIsVerified && (
                        <MdVerified
                          style={{
                            marginLeft: 6,
                            verticalAlign: 'middle',
                            color: video.author.badge_color || 'currentColor'
                          }}
                          title={video.author.badge_label || 'Cuenta verificada'}
                        />
                      )}
                    </strong>
                  </Link>
                  <span>{new Date(video.created_at || video.createdAt).toLocaleDateString('es-ES')}</span>
                </div>
              </div>

              {/* Reactions - solo visible si está autenticado */}
              {isAuthenticated ? (
                <div className="video-reactions-bar">
                  {REACTIONS.map(r => (
                    <button
                      key={r.id}
                      className={`reaction-btn ${userReaction === r.id ? 'active' : ''}`}
                      onClick={() => handleReaction(r.id)}
                      title={r.label}
                    >
                      <span className="reaction-emoji">{r.emoji}</span>
                      <span className="reaction-count">{reactionCounts[r.id] || 0}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="login-prompt-reactions">
                  <span>🔒 Inicia sesión para reaccionar</span>
                </div>
              )}

              {/* Actions */}
              <div className="video-modal-actions">
                <button className="action-btn">
                  <FiMessageCircle />
                  <span>{comments.length}</span>
                </button>

                <button className="action-btn" onClick={handleShare}>
                  <FiShare2 />
                  <span>Compartir</span>
                </button>

                <button 
                  className={`action-btn ${isSaved ? 'active' : ''}`}
                  onClick={handleToggleSave}
                >
                  <FiBookmark />
                </button>

                {isOwner && (
                  <>
                    <button className="action-btn" onClick={handleEdit} title="Editar">
                      <FiEdit2 />
                    </button>
                    <button className="action-btn" onClick={handleDelete} title="Eliminar">
                      <FiTrash2 />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Comments */}
            <div className="video-modal-comments">
              <CommentSection
                postId={video.id}
                comments={comments}
                onAddComment={handleAddComment}
                onEditComment={handleEditComment}
                onDeleteComment={handleDeleteComment}
                onRefresh={loadData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
